import { BaseService } from '@/services/base';
import { UserProfile, TestResult, TrialWeekProof } from '@/models/UserTypes';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase';
import { logger } from '@/utils/logger';
import type { LicensingAnswers } from '@/features/licensing/types';

// ============================================================================
// Cloud Function Request/Response Types
// ============================================================================

/**
 * Request for updateUserProfile Cloud Function
 *
 * These interfaces are HAND-MAINTAINED mirrors of the backend contract and have
 * drifted silently before, so the licensing half is not spelled out here: both
 * requests extend `LicensingAnswers`, whose fields are pinned to the shared
 * `LICENSING_PROFILE_FIELDS` constant (and to aswb-backend's copy of it) by
 * `src/__tests__/unit/features/licensing/drift.test.ts`.
 */
interface UpdateProfileRequest extends LicensingAnswers {
  name?: string;
  targetExam?: string;
  examDate?: string;
  studyGoal?: string;
  educationLevel?: string;
  preferredStudyTime?: string;
  challengingTopics?: string[];
  learningStyle?: string;
  onboardingCompleted?: boolean;
  timezone?: string;
  diagnostic?: {
    score: number;
    total: number;
    readiness: number;
    weakAreas: string[];
    examType: string | null;
    completedAt: string;
  };
  notificationPreferences?: {
    pushEnabled?: boolean;
    emailEnabled?: boolean;
    studyReminders?: boolean;
    progressUpdates?: boolean;
    promotions?: boolean;
    reminderTime?: string;
  };
}

/**
 * Response from updateUserProfile Cloud Function
 */
interface UpdateProfileResponse {
  success: boolean;
  message: string;
  updatedFields: string[];
}

/**
 * Request for createTrialUser Cloud Function
 */
interface CreateTrialUserRequest extends LicensingAnswers {
  email: string;
  name?: string;
  referralCode?: string;
  marketingEmailConsent?: boolean;
  transactionalEmailConsent?: boolean;
  examDate?: string;
  diagnostic?: {
    score: number;
    total: number;
    readiness: number;
    weakAreas: string[];
    examType: string | null;
    completedAt: string;
  };
  onboardingCompleted?: boolean;
  utmParams?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
}

/**
 * Response from createTrialUser Cloud Function
 */
interface CreateTrialUserResponse {
  success: boolean;
  userId: string;
  trialStartDate: string;
  trialEndDate: string;
  message: string;
}

/**
 * Response from checkTrialStatus Cloud Function
 */
interface CheckTrialStatusResponse {
  isTrialActive: boolean;
  daysRemaining: number;
  trialEndDate: string | null;
  hasExpired: boolean;
}

/**
 * Response from getUserProfile Cloud Function
 */
interface GetUserProfileResponse {
  id: string;
  email: string;
  profile: {
    name: string;
    targetExam: string | null;
    examDate: string | null;
    examDates: Record<string, { date: string; setAt: string }> | null;
    /** Server-reconciled soonest scheduled exam (epoch ms). Prefer this over the
     *  legacy examDate/examDates client resolution. */
    nextScheduledExamMs?: number | null;
    studyGoal: string | null;
    educationLevel: string | null;
    preferredStudyTime: string | null;
    challengingTopics: string[];
    learningStyle: string | null;
    onboardingCompleted: boolean;
    lastUpdated: string | null;
    timezone: string | null;
    schoolCode?: string | null;
    diagnostic?: {
      score: number;
      total: number;
      readiness: number;
      weakAreas: string[];
      examType: string | null;
      completedAt: string;
    } | null;
    notificationPreferences: {
      pushEnabled: boolean;
      emailEnabled: boolean;
      studyReminders: boolean;
      progressUpdates: boolean;
      promotions: boolean;
      reminderTime: string | null;
    } | null;
  } | null;
  subscriptionStatus: string | null;
  isSubscribed: boolean;
  /**
   * Authoritative server-computed access decision from getUserProfile.
   * Clients should read this rather than re-deriving from subscription/trial
   * fields. See hasFullAccess() in utils/firebase/subscription.ts.
   */
  hasFullAccess: boolean;
  /**
   * Server-computed flag: true when the user has permanent one-time ($129)
   * access. Distinct from hasFullAccess (which is also true for active trials).
   * The backend returns it in getUserProfile.ts; mapped through so the /pricing
   * redirect can send lifetime buyers to the dashboard without keeping trial
   * users (who lack this) on /pricing.
   */
  hasLifetimeAccess: boolean;
  /**
   * Authoritative access reason for product surfaces (trial dashboard gate).
   * Top-level on the wire, per the backend's getUserProfile contract
   * (lifetime | subscription | comp | bonus | trial | none).
   */
  accessReason?: string | null;
  stripeCustomerId: string | null;
  subscriptionId: string | null;
  trialStartDate: string | null;
  trialEndDate: string | null;
  trialWeek?: TrialWeekProof | null;
  isTrialActive: boolean;
  totalQuestionsAttempted: number;
  correctAnswers: number;
  studyStreak: number;
  masteryLevel: number;
  averageScore: number;
  /** Server-computed LIFETIME accuracy (integer 0-100). Read this instead of
   *  recomputing correctAnswers / totalQuestionsAttempted on the client. */
  accuracy?: number;
  /** Server-computed mastery as an integer percent (0-100). */
  masteryPercent?: number;
  completedTests: number;
  totalStudyHours: number;
  consent: {
    marketingEmailConsent: boolean;
    transactionalEmailConsent: boolean;
    termsAcceptedAt: string | null;
    privacyAcceptedAt: string | null;
  } | null;
  /** ISO end of a study-email pause still in force (setStudyPause), else null. Absent on older backends. */
  studyPausedUntil?: string | null;
  createdAt: string | null;
  lastLoginAt: string | null;
  lastActive: string | null;
}

interface UserProgress {
  totalQuestionsAnswered: number;
  correctAnswers: number;
  studyStreak: number;
  lastStudyDate: string;
  categoryProgress: Record<string, {
    attempted: number;
    correct: number;
  }>;
}

interface UserSettings {
  notifications: boolean;
  darkMode: boolean;
  studyReminders: boolean;
  reminderTime?: string;
}

export class UserService extends BaseService {
  private static instance: UserService;
  private currentProfile: UserProfile | null = null;
  private profileListeners: ((profile: UserProfile | null) => void)[] = [];

  private constructor() {
    super();
  }

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * @deprecated Use getFullUserProfile() instead which calls the Cloud Function.
   * This direct Firestore access method is kept for backwards compatibility.
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const profile = userDoc.exists() ? userDoc.data() as UserProfile : null;
      this.currentProfile = profile;
      this.notifyProfileListeners(profile);
      return profile;
    } catch (error) {
      this.handleError(error, 'getUserProfile');
    }
  }

  /**
   * Get full user profile via Cloud Function
   * Returns complete user data including profile, subscription, trial, stats
   */
  async getFullUserProfile(): Promise<GetUserProfileResponse | null> {
    try {
      logger.info('UserService', 'Fetching full user profile via Cloud Function');
      const getUserProfileFn = httpsCallable<void, GetUserProfileResponse>(
        functions,
        'getUserProfile'
      );
      const result = await getUserProfileFn();
      logger.info('UserService', 'Full user profile retrieved', {
        userId: result.data.id,
        hasProfile: !!result.data.profile,
        isSubscribed: result.data.isSubscribed,
        isTrialActive: result.data.isTrialActive
      });
      return result.data;
    } catch (error) {
      logger.error('UserService', 'Failed to get full user profile', { error });
      return null;
    }
  }

  // ==========================================================================
  // Cloud Function Methods (preferred for cross-platform consistency)
  // ==========================================================================

  /**
   * Update user profile via Cloud Function
   * Preferred method - consistent with iOS/KMP
   */
  async updateProfile(updates: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    try {
      logger.info('UserService', 'Updating profile via Cloud Function', {
        fields: Object.keys(updates)
      });
      const updateProfileFn = httpsCallable<UpdateProfileRequest, UpdateProfileResponse>(
        functions,
        'updateUserProfile'
      );
      const result = await updateProfileFn(updates);
      logger.info('UserService', 'Profile updated', {
        success: result.data.success,
        updatedFields: result.data.updatedFields
      });
      return result.data;
    } catch (error) {
      logger.error('UserService', 'Failed to update profile', { error });
      throw error;
    }
  }

  /**
   * Create trial user via Cloud Function
   * This handles all trial setup including emails, tracking, etc.
   */
  async createTrialUser(request: CreateTrialUserRequest): Promise<CreateTrialUserResponse> {
    try {
      logger.info('UserService', 'Creating trial user via Cloud Function', {
        email: request.email,
        hasReferralCode: !!request.referralCode
      });
      const createTrialFn = httpsCallable<CreateTrialUserRequest, CreateTrialUserResponse>(
        functions,
        'createTrialUser'
      );
      const result = await createTrialFn(request);
      logger.info('UserService', 'Trial user created', {
        userId: result.data.userId,
        trialEndDate: result.data.trialEndDate
      });
      return result.data;
    } catch (error) {
      logger.error('UserService', 'Failed to create trial user', { error });
      throw error;
    }
  }

  /**
   * Check trial status via Cloud Function
   */
  async checkTrialStatus(): Promise<CheckTrialStatusResponse> {
    try {
      logger.info('UserService', 'Checking trial status via Cloud Function');
      const checkTrialFn = httpsCallable<void, CheckTrialStatusResponse>(
        functions,
        'checkTrialStatus'
      );
      const result = await checkTrialFn();
      logger.info('UserService', 'Trial status checked', {
        isActive: result.data.isTrialActive,
        daysRemaining: result.data.daysRemaining
      });
      return result.data;
    } catch (error) {
      logger.error('UserService', 'Failed to check trial status', { error });
      throw error;
    }
  }

  // ==========================================================================
  // Legacy Direct Firestore Methods (deprecated - use Cloud Function methods)
  // ==========================================================================

  /**
   * @deprecated Use updateProfile() instead which calls the Cloud Function.
   */
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, updates);
      const updatedProfile = await this.getUserProfile(userId);
      this.notifyProfileListeners(updatedProfile);
    } catch (error) {
      this.handleError(error, 'updateUserProfile');
    }
  }

  /**
   * Get user progress
   */
  async getUserProgress(userId: string): Promise<UserProgress> {
    try {
      const progressDoc = await getDoc(doc(db, 'users', userId, 'progress', 'latest'));
      return progressDoc.exists() ? progressDoc.data() as UserProgress : this.initializeProgress();
    } catch (error) {
      this.handleError(error, 'getUserProgress');
    }
  }

  /**
   * Update user settings
   */
  async updateUserSettings(userId: string, settings: Partial<UserSettings>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { settings });
    } catch (error) {
      this.handleError(error, 'updateUserSettings');
    }
  }

  /**
   * Get test results
   */
  async getTestResults(userId: string, limit?: number): Promise<TestResult[]> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      
      if (!userData?.testResults) {
        return [];
      }

      let results = Object.entries(userData.testResults)
        .map(([id, result]) => ({
          ...(result as TestResult),
          id: id
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      if (limit) {
        results = results.slice(0, limit);
      }

      return results;
    } catch (error) {
      this.handleError(error, 'getTestResults');
    }
  }

  /**
   * Subscribe to profile changes
   */
  subscribeToProfile(callback: (profile: UserProfile | null) => void): () => void {
    this.profileListeners.push(callback);
    if (this.currentProfile !== null) {
      callback(this.currentProfile);
    }
    return () => {
      this.profileListeners = this.profileListeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Initialize user progress
   */
  private initializeProgress(): UserProgress {
    return {
      totalQuestionsAnswered: 0,
      correctAnswers: 0,
      studyStreak: 0,
      lastStudyDate: new Date().toISOString(),
      categoryProgress: {}
    };
  }

  /**
   * Notify profile listeners
   */
  private notifyProfileListeners(profile: UserProfile | null): void {
    this.profileListeners.forEach(listener => {
      try {
        listener(profile);
      } catch (error) {
        logger.error('UserService', 'Error in profile listener', { error });
      }
    });
  }
} 